import { useEffect } from "react";

export interface ShortcutHandler {
  /** Lowercase key to match against e.key */
  key: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
  /** When true, the shortcut fires even if the user is typing inside an input/textarea/contenteditable. Default: false. */
  allowInInputs?: boolean;
  /** Prevent default browser behavior. Default: true */
  preventDefault?: boolean;
  handler: (event: KeyboardEvent) => void;
}

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
};

/**
 * Global keyboard shortcuts hook. Handlers fire on keydown.
 * On macOS, `ctrl` matches metaKey OR ctrlKey to feel native; pass `meta: true` for strictly Cmd.
 */
export const useKeyboardShortcuts = (shortcuts: ShortcutHandler[]) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const editable = isEditableTarget(event.target);
      const pressedKey = event.key.toLowerCase();

      for (const sc of shortcuts) {
        if (sc.key.toLowerCase() !== pressedKey) continue;
        if (sc.ctrl !== undefined) {
          const ctrlOrMeta = event.ctrlKey || event.metaKey;
          if (sc.ctrl && !ctrlOrMeta) continue;
          if (!sc.ctrl && ctrlOrMeta) continue;
        }
        if (sc.meta !== undefined && sc.meta !== event.metaKey) continue;
        if (sc.shift !== undefined && sc.shift !== event.shiftKey) continue;
        if (sc.alt !== undefined && sc.alt !== event.altKey) continue;
        if (editable && !sc.allowInInputs) continue;

        if (sc.preventDefault !== false) event.preventDefault();
        sc.handler(event);
        break;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [shortcuts]);
};
